/**
 * UsersPage — "Kullanıcılar": çoklu admin (alt admin / subadmin) yönetim sayfası.
 *
 * Yalnız ana admin (role='admin') erişir (App.tsx rota guard'ı). İki sekme:
 * - "Alt Adminler": personel listesinden role==='subadmin' + getAdminPanelList()
 *   birleşimi. Satır aksiyonları: İşlem Geçmişi, Yetkileri Düzenle
 *   (AdminUserPermissionsModal), Kullanıcılığı Kaldır (MUI onay dialogu →
 *   role:'employee' + setAdminPanel(false,{})).
 * - "Şube Yöneticileri": role==='manager' personel — yalnız İşlem Geçmişi
 *   (yönetici atama/yetkileri Şubeler modülünde kalır).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  getPersonnel,
  getAdminPanelList,
  setAdminPanel,
  updatePersonnel,
  type Personnel,
} from '../../services/migration/personnelDataService';
import { getBranches } from '../../services/migration/branchDataService';
import { summarizeAllowedModules, type AdminPermissionsMap } from './adminModuleMap';
import AdminUserPermissionsModal from './AdminUserPermissionsModal';
import SubAdminActivityLogModal from './SubAdminActivityLogModal';

interface SubAdminRow {
  uid: string;
  name: string;
  email: string;
  isActive: boolean;
  modules: string[];
}

/** Şube yöneticisi satırı (yalnız işlem geçmişi görüntülenir). */
interface ManagerRow {
  uid: string;
  name: string;
  email: string;
  isActive: boolean;
  /** Yönettiği şubenin adı (personel.branchesId → şube eşlemesi). */
  branchName: string;
}

/** İşlem geçmişi modalı hedefi: hangi kullanıcı + hangi log kaynağı. */
interface ActivityTarget {
  uid: string;
  name: string;
  variant: 'subadmin' | 'manager';
}

const MAX_VISIBLE_CHIPS = 4;

const UsersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { userDetails } = useAuth();
  const isTr = i18n.language.startsWith('tr');

  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState<SubAdminRow[]>([]);
  const [managerRows, setManagerRows] = useState<ManagerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUid, setEditUid] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<SubAdminRow | null>(null);
  const [removing, setRemoving] = useState(false);
  const [activityTarget, setActivityTarget] = useState<ActivityTarget | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const loadData = useCallback(async () => {
    if (!userDetails?.companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [personnel, panelList, branches] = await Promise.all([
        getPersonnel(userDetails.companyId),
        getAdminPanelList().catch(() => []),
        getBranches(userDetails.companyId).catch(() => []),
      ]);
      const branchNameById = new Map(branches.map((b) => [b.id, b.name]));
      const permsByUid = new Map<string, AdminPermissionsMap | null>();
      for (const row of panelList) {
        permsByUid.set(
          row.firebaseUid,
          row.isEnabled && row.settings && typeof row.settings === 'object' ? row.settings : null
        );
      }
      const subAdmins: SubAdminRow[] = (personnel as Personnel[])
        .filter((p) => p.role === 'subadmin' && !p.isDeleted)
        .map((p) => ({
          uid: p.id,
          name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim(),
          email: p.email || '',
          isActive: p.isActive !== false,
          modules: summarizeAllowedModules(permsByUid.get(p.id) || null, isTr ? 'tr' : 'en'),
        }));
      setRows(subAdmins);

      // Şube yöneticileri sekmesi (yalnız işlem geçmişi — yönetim Şubeler modülünde).
      const managers: ManagerRow[] = (personnel as Personnel[])
        .filter((p) => p.role === 'manager' && !p.isDeleted)
        .map((p) => ({
          uid: p.id,
          name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim(),
          email: p.email || '',
          isActive: p.isActive !== false,
          branchName: (p.branchesId && branchNameById.get(p.branchesId)) || '',
        }));
      setManagerRows(managers);
    } catch (err: any) {
      console.error('[UsersPage] Liste yüklenemedi:', err);
      setError(err?.response?.data?.message || err?.message || t('users.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [userDetails?.companyId, isTr, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      // 1) İzin haritasını kapat (CompanyAdminOnly korumalı uç).
      await setAdminPanel(removeTarget.uid, false, {});
      // 2) Rolü employee'ye düşür.
      await updatePersonnel(removeTarget.uid, { role: 'employee' });
      setSnackbar({ message: t('users.messages.removeSuccess'), severity: 'success' });
      setRemoveTarget(null);
      loadData();
    } catch (err: any) {
      console.error('[UsersPage] Kullanıcılık kaldırma hatası:', err);
      setSnackbar({
        message: err?.response?.data?.message || err?.message || t('users.messages.removeError'),
        severity: 'error',
      });
    } finally {
      setRemoving(false);
    }
  };

  const emptyState = useMemo(
    () => !loading && !error && rows.length === 0,
    [loading, error, rows.length]
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Başlık + Yeni Kullanıcı Ekle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PeopleIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t('users.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('users.subtitle')}
            </Typography>
          </Box>
        </Box>
        {tab === 0 && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setEditUid(null);
              setModalOpen(true);
            }}
          >
            {t('users.addUser')}
          </Button>
        )}
      </Box>

      {/* Sekmeler: Alt Adminler / Şube Yöneticileri */}
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<PeopleIcon />} iconPosition="start" label={t('users.tabs.subAdmins')} />
        <Tab icon={<SupervisorAccountIcon />} iconPosition="start" label={t('users.tabs.managers')} />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : tab === 1 ? (
        // ── Şube Yöneticileri sekmesi ─────────────────────────────────────────
        managerRows.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <SupervisorAccountIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              {t('users.managersEmptyTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('users.managersEmptyHint')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('users.columns.user')}</TableCell>
                  <TableCell>{t('users.columns.email')}</TableCell>
                  <TableCell>{t('users.columns.branch')}</TableCell>
                  <TableCell>{t('users.columns.status')}</TableCell>
                  <TableCell align="right">{t('users.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {managerRows.map((row) => (
                  <TableRow key={row.uid} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 34, height: 34, fontSize: 15 }}>
                          {(row.name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography fontWeight={600}>{row.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      {row.branchName ? (
                        <Chip size="small" label={row.branchName} color="info" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.isActive ? t('users.statusActive') : t('users.statusInactive')}
                        color={row.isActive ? 'success' : 'default'}
                        variant={row.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('users.activityLog.openButton')}>
                        <IconButton
                          color="info"
                          onClick={() =>
                            setActivityTarget({ uid: row.uid, name: row.name, variant: 'manager' })
                          }
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : emptyState ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <PeopleIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {t('users.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('users.emptyHint')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setEditUid(null);
              setModalOpen(true);
            }}
          >
            {t('users.addUser')}
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('users.columns.user')}</TableCell>
                <TableCell>{t('users.columns.email')}</TableCell>
                <TableCell>{t('users.columns.modules')}</TableCell>
                <TableCell>{t('users.columns.status')}</TableCell>
                <TableCell align="right">{t('users.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.uid} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34, fontSize: 15 }}>
                        {(row.name || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography fontWeight={600}>{row.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 420 }}>
                      {row.modules.length === 0 ? (
                        <Chip size="small" label={t('users.noModules')} variant="outlined" color="warning" />
                      ) : (
                        <>
                          {row.modules.slice(0, MAX_VISIBLE_CHIPS).map((m) => (
                            <Chip key={m} size="small" label={m} variant="outlined" />
                          ))}
                          {row.modules.length > MAX_VISIBLE_CHIPS && (
                            <Tooltip title={row.modules.slice(MAX_VISIBLE_CHIPS).join(', ')}>
                              <Chip
                                size="small"
                                label={`+${row.modules.length - MAX_VISIBLE_CHIPS}`}
                                color="primary"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.isActive ? t('users.statusActive') : t('users.statusInactive')}
                      color={row.isActive ? 'success' : 'default'}
                      variant={row.isActive ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('users.activityLog.openButton')}>
                      <IconButton
                        color="info"
                        onClick={() =>
                          setActivityTarget({ uid: row.uid, name: row.name, variant: 'subadmin' })
                        }
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('users.editPermissions')}>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setEditUid(row.uid);
                          setModalOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('users.removeUser')}>
                      <IconButton color="error" onClick={() => setRemoveTarget(row)}>
                        <PersonRemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Yetki atama/düzenleme modalı */}
      <AdminUserPermissionsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditUid(null);
        }}
        onSaved={loadData}
        editUid={editUid}
      />

      {/* İşlem geçmişi modalı (alt admin / şube yöneticisi — variant'a göre kaynak) */}
      <SubAdminActivityLogModal
        open={Boolean(activityTarget)}
        onClose={() => setActivityTarget(null)}
        uid={activityTarget?.uid || null}
        userName={activityTarget?.name || ''}
        variant={activityTarget?.variant || 'subadmin'}
      />

      {/* Kullanıcılığı kaldırma onayı */}
      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)}>
        <DialogTitle>{t('users.removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('users.removeConfirmText', { name: removeTarget?.name || '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing} color="inherit">
            {t('common.cancel', { defaultValue: 'İptal' })}
          </Button>
          <Button
            onClick={handleRemove}
            color="error"
            variant="contained"
            disabled={removing}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <PersonRemoveIcon />}
          >
            {t('users.removeUser')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity || 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
